import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { ButtonComponent, ButtonType } from '../../../../../shared/components/button/button.component';
import { PmsSetup, PmsType } from '../../../types/pms';
import { MewsConnectComponent } from './pms-components/mews/mews.component';
import { OperaCloudConnectComponent } from './pms-components/opera-cloud/opera-cloud.component';
import { GuestlineConnectComponent } from './pms-components/guestline/guestline.component';
import { ShijiConnectComponent } from './pms-components/shiji/shiji.component';
import { StayNTouchConnectComponent } from './pms-components/stay-n-touch/stay-n-touch.component';

@Component({
    selector: 'juyo-connect-step',
    styleUrl: './connect-step.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        MewsConnectComponent,
        OperaCloudConnectComponent,
        GuestlineConnectComponent,
        ShijiConnectComponent,
        StayNTouchConnectComponent,
    ],
    template: `
        <div class="step-body">
            @switch (pmsType()) {
                @case (PmsType.Mews) {
                    <juyo-mews-connect (valueChange)="currentValue.set($event)" />
                }
                @case (PmsType.OperaCloud) {
                    <juyo-opera-cloud-connect (valueChange)="currentValue.set($event)" />
                }
                @case (PmsType.Guestline) {
                    <juyo-guestline-connect (valueChange)="currentValue.set($event)" />
                }
                @case (PmsType.Shiji) {
                    <juyo-shiji-connect (valueChange)="currentValue.set($event)" />
                }
                @case (PmsType.StayNTouch) {
                    <juyo-stay-n-touch-connect
                        [propertyName]="propertyName()"
                        (valueChange)="currentValue.set($event)"
                    />
                }
                @default {
                    <p class="unsupported-pms">Configuration not yet available for this PMS.</p>
                }
            }
        </div>

        <div class="step-footer">
            <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="back.emit()">
                <span class="material-symbols-outlined">arrow_left_alt</span> Back
            </juyo-button>
            <div class="footer-actions">
                @if (showSkip()) {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="skip.emit()">
                        Skip for now
                    </juyo-button>
                } @else {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="cancel.emit()"> Cancel </juyo-button>
                }
                <juyo-button [disabled]="!canSubmit() || saving()" [isLoading]="saving()" (clicked)="onSubmit()">
                    Connect PMS <span class="material-symbols-outlined">check_small</span>
                </juyo-button>
            </div>
        </div>
    `,
})
export class ConnectStepComponent {
    public pmsType = input.required<PmsType>();
    public saving = input<boolean>(false);
    public showSkip = input<boolean>(true);
    public propertyName = input<string>('');

    public submit = output<PmsSetup>();
    public back = output<void>();
    public cancel = output<void>();
    public skip = output<void>();

    protected readonly ButtonType = ButtonType;
    protected readonly PmsType = PmsType;

    protected currentValue = signal<PmsSetup | null>(null);
    protected canSubmit = computed(() => this.currentValue() !== null);

    constructor() {
        effect(() => {
            this.pmsType();
            this.currentValue.set(null);
        });
    }

    protected onSubmit(): void {
        const value = this.currentValue();
        if (value) this.submit.emit(value);
    }
}
